import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import './DeleteAccount.css';

const API_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:5000/api');

const DeleteAccount = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [password, setPassword] = useState('');
  const [confirmDelete, setConfirmDelete] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleDelete = async (e) => {
    e.preventDefault();
    setError('');

    if (!password) {
      setError('Password is required');
      return;
    }

    if (confirmDelete !== 'DELETE') {
      setError('Please type DELETE to confirm');
      return;
    }

    setLoading(true);
    try {
      await axios.delete(
        `${API_URL}/auth/delete-account`,
        {
          data: { password },
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`
          }
        }
      );

      // Logout and redirect to login
      logout();
      navigate('/login', { state: { message: 'Your account has been deleted successfully.' } });
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete account. Please try again.');
      setLoading(false);
    }
  };

  return (
    <div className="container">
      <div className="delete-account-header">
        <h1>Delete Account</h1>
        <p>Permanently delete your account and all associated data</p>
      </div>

      <div className="delete-account-content">
        <div className="warning-box">
          <h2>⚠️ Warning: This action cannot be undone</h2>
          <p>Deleting your account will permanently remove:</p>
          <ul>
            <li>Your profile and account information</li>
            <li>All your saved books</li>
            <li>All your generated summaries</li>
            <li>All your questions and answers</li>
            <li>All your ratings</li>
          </ul>
          <p><strong>This action is irreversible.</strong></p>
        </div>

        {!showConfirm ? (
          <div className="delete-instructions">
            <h2>How to Delete Your Account</h2>
            <ol>
              <li>Enter your current password below</li>
              <li>Type "DELETE" in the confirmation field</li>
              <li>Click the "Delete My Account" button</li>
              <li>Your account will be permanently deleted</li>
            </ol>
            <button 
              className="btn btn-danger"
              onClick={() => setShowConfirm(true)}
            >
              I Understand, Proceed to Delete
            </button>
          </div>
        ) : (
          <form onSubmit={handleDelete} className="delete-form">
            <div className="form-group">
              <label htmlFor="password">Enter Your Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password to confirm"
                className="form-input"
                disabled={loading}
                required
              />
            </div>

            <div className="form-group">
              <label htmlFor="confirmDelete">Type "DELETE" to Confirm</label>
              <input
                type="text"
                id="confirmDelete"
                value={confirmDelete}
                onChange={(e) => setConfirmDelete(e.target.value)}
                placeholder="Type DELETE"
                className="form-input"
                disabled={loading}
                required
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className="delete-actions">
              <button 
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowConfirm(false);
                  setPassword('');
                  setConfirmDelete('');
                  setError('');
                }}
                disabled={loading}
              >
                Cancel
              </button>
              <button 
                type="submit" 
                className="btn btn-danger"
                disabled={loading || confirmDelete !== 'DELETE'}
              >
                {loading ? 'Deleting...' : 'Delete My Account'}
              </button>
            </div>
          </form>
        )}

        <div className="back-link">
          <button 
            className="btn btn-secondary"
            onClick={() => navigate('/settings')}
          >
            ← Back to Settings
          </button>
        </div>
      </div>
    </div>
  );
};

export default DeleteAccount;
